
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { AffiliateMarketingAsset } from '../types';
import { FileText, Image, File, X, Upload } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface AffiliateMarketingAssetsSectionProps {
  assets: AffiliateMarketingAsset[];
  onAssetsChange: (assets: AffiliateMarketingAsset[]) => void;
}

export const AffiliateMarketingAssetsSection: React.FC<AffiliateMarketingAssetsSectionProps> = ({
  assets,
  onAssetsChange
}) => {
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    
    // In a real application, you would upload these files to a server
    // Here we'll simulate the upload with a timeout
    setTimeout(() => {
      const newAssets: AffiliateMarketingAsset[] = Array.from(files).map(file => {
        const isImage = file.type.startsWith('image/');
        const isDocument = file.type === 'application/pdf' || 
                           file.type === 'application/msword' || 
                           file.type.includes('officedocument');
                          
        return {
          id: `asset-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          type: isImage ? 'banner' : (isDocument ? 'text' : 'email-template'),
          name: file.name,
          url: URL.createObjectURL(file),
          size: formatFileSize(file.size),
          uploadedAt: new Date(),
          isActive: true  // Add the required isActive property
        };
      });
      
      onAssetsChange([...assets, ...newAssets]);
      setUploading(false);
      
      toast({
        title: "Assets uploaded",
        description: `Successfully uploaded ${newAssets.length} asset(s)`,
      });
      
      // Reset the input
      event.target.value = '';
    }, 1500);
  };
  
  const handleRemoveAsset = (id: string) => {
    onAssetsChange(assets.filter(asset => asset.id !== id));
  };
  
  const getAssetIcon = (type: string) => {
    switch (type) {
      case 'banner':
        return <Image className="h-4 w-4" />;
      case 'email-template':
        return <FileText className="h-4 w-4" />;
      case 'text':
        return <File className="h-4 w-4" />;
      default:
        return <File className="h-4 w-4" />;
    }
  };
  
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2">
        <Label htmlFor="asset-upload">Upload Marketing Assets</Label>
        <div className="flex gap-2">
          <Input
            id="asset-upload"
            type="file"
            multiple
            onChange={handleFileChange}
            accept="image/*,.pdf,.doc,.docx,.ppt,.pptx"
            disabled={uploading}
          />
          <Button variant="outline" disabled={uploading}>
            {uploading ? (
              <>Uploading...</>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </>
            )}
          </Button>
        </div>
        <p className="text-sm text-gray-500">
          Upload banners, templates, and documents that affiliates can use to promote your products.
        </p>
      </div>
      
      {assets.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asset</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assets.map((asset) => (
                  <TableRow key={asset.id}>
                    <TableCell className="flex items-center gap-2">
                      {getAssetIcon(asset.type)}
                      <span className="font-medium">{asset.name}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {asset.type}
                      </Badge>
                    </TableCell>
                    <TableCell>{asset.size || 'N/A'}</TableCell>
                    <TableCell>
                      {asset.uploadedAt ? new Date(asset.uploadedAt).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveAsset(asset.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
